// src/pages/Todo/useReminderStream.ts
// Surfaces toasts when fresh task notifications arrive in the
// `notifications` Redux slice. Source of truth is the slice, populated by:
//   - one-time hydration (NotificationsBell)
//   - WS frame `task_notification` (useNotificationSocket dispatches pushNotification)
// No polling.
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { selectNotifications } from "../redux/slices/notifications";
import type { TaskNotification } from "../api/tasks";

export function useReminderStream(onClick?: (taskId: string) => void) {
  const items = useSelector(selectNotifications) as TaskNotification[];
  const seen = useRef<Set<string>>(new Set());
  const firstRun = useRef(true);

  useEffect(() => {
    // Skip the first run so we don't toast every notification on hydrate.
    if (firstRun.current) {
      items.forEach(i => seen.current.add(i.id));
      firstRun.current = false;
      return;
    }
    for (const r of items) {
      if (seen.current.has(r.id)) continue;
      seen.current.add(r.id);

      const action = onClick
        ? { label: "Open", onClick: () => onClick(r.task_id) }
        : undefined;
      const kind = r.kind;

      if (kind === "OVERDUE") {
        toast.error(r.title, { description: r.body || undefined, action });
      } else if (kind === "DUE_SOON" || kind === "REMINDER") {
        toast(r.title, { description: r.body || undefined, action, icon: "⏰" });
      } else if (kind === "ASSIGNED") {
        toast.success(r.title, { description: r.body || undefined, action });
      } else {
        toast(r.title, { description: r.body || undefined, action });
      }
    }
  }, [items, onClick]);
}
